import { Link, useSearchParams } from "@/lib/router-compat";
import { FunnelForm } from "@/components/FunnelForm";
import { motion } from "framer-motion";
import { useState } from "react";
import { SEOHead } from "@/components/SEOHead";
import { testimoniosData } from "@/components/TestimoniosCarousel";
import { Quote, Star, Play } from "lucide-react";
import { GoogleLogo } from "@/components/GoogleLogo";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { ClientPlyr as Plyr } from "@/components/ClientPlyr";

const VSL_THUMBNAIL =
  "https://assets.cdn.filesafe.space/UmeUICnWy44Ung9XrMjl/media/6a9f29f4db940b68053bb9c6.png";

export default function Demo() {
  const [formOpen, setFormOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const isDistribuidores = searchParams.get("a") === "distribuidores";

  const heroTitle = isDistribuidores
    ? "Ayudamos a dueños de distribuidoras a conseguir más trabajos con IA, sitios web y reseñas de 5 estrellas."
    : "Ayudamos a dueños de empresas de servicios a conseguir más trabajos con IA, sitios web y reseñas de 5 estrellas.";

  const openForm = () => {
    import("@/lib/analytics").then(({ trackEvent }) =>
      trackEvent("Demo", "open_form", "Ver cómo funciona"),
    );
    setFormOpen(true);
  };

  const videoTestimonials = testimoniosData.filter((t) => t.videoUrl);
  const textTestimonials = testimoniosData.filter((t) => !t.videoUrl);
  const orderedTestimonials = [...videoTestimonials, ...textTestimonials].slice(
    0,
    6,
  );

  return (
    <div className="min-h-screen bg-[#08080A] text-foreground flex flex-col selection:bg-primary/30 selection:text-primary-foreground">
      <SEOHead
        title="Agendar llamada — CreceLocal"
        description="Ayudamos a dueños de empresas de servicios y distribuidoras a conseguir más trabajos con IA, sitios web inteligentes y reseñas de 5 estrellas. Por solo $390.000 ARS/mes."
        canonical="/demo"
      />

      <main className="flex-1 pt-16 pb-20 relative overflow-hidden">
        {/* Background */}
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-tl from-[#FB7185]/10 to-transparent rounded-full blur-[120px] pointer-events-none" />

        <div className="container mx-auto px-6 max-w-[1100px] relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl mx-auto mb-10"
          >
            <h1 className="text-[28px] leading-[1.15] sm:text-5xl lg:text-6xl font-medium tracking-tight mb-4 text-foreground sm:leading-[1.08]">
              {isDistribuidores ? (
                <>
                  Ayudamos a dueños de distribuidoras a conseguir más trabajos
                  con{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-[#FB7185]">
                    IA, sitios web y reseñas de 5 estrellas.
                  </span>
                </>
              ) : (
                <>
                  Ayudamos a dueños de empresas de servicios a conseguir más
                  trabajos con{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-[#FB7185]">
                    IA, sitios web y reseñas de 5 estrellas.
                  </span>
                </>
              )}
            </h1>

            <p className="text-2xl sm:text-3xl font-medium text-muted-foreground mt-6">
              Por solo <span className="text-foreground">$390.000 ARS/mes</span>
            </p>
          </motion.div>

          {/* VSL placeholder con thumbnail */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl mx-auto mb-8"
          >
            <div className="text-left mb-3">
              <p className="text-sm font-mono uppercase tracking-wider text-muted-foreground">
                Paso 1: Mirá este video corto para ver qué hacemos y cómo te
                conseguimos más trabajos.
              </p>
            </div>
            <button
              onClick={openForm}
              className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-[#0F0F11] aspect-video group block"
            >
              <img
                src={VSL_THUMBNAIL}
                alt="Video: cómo conseguimos más trabajos para empresas de servicios"
                className="w-full h-full object-cover blur-[2px] scale-105 group-hover:blur-[1px] transition-all duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-9 h-9 text-white ml-1"
                    fill="currentColor"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </button>
          </motion.div>

          {/* CTA principal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              onClick={openForm}
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold hover:-translate-y-[2px] transition-all hover:shadow-[0_12px_32px_rgba(16,185,129,0.3)] rounded-xl px-10 py-4 text-lg inline-flex items-center justify-center gap-2"
            >
              Ver cómo funciona
            </button>
            <p className="text-sm text-muted-foreground mt-4 flex items-center justify-center gap-2">
              <GoogleLogo className="w-4 h-4" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              Reseñas reales de clientes abajo
            </p>
          </motion.div>
        </div>

        {/* Formulario modal */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="max-w-[640px] w-[calc(100%-2rem)] bg-[#0F0F11] border border-white/10 p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">
              Formulario de diagnóstico
            </DialogTitle>
            <div className="p-6 md:p-10">
              <h2 className="text-2xl font-medium text-center mb-2">
                Ayudamos a Dueños de Negocios de Servicios a conseguir más
                clientes
              </h2>
              <p className="text-muted-foreground text-center mb-6">
                Por sólo{" "}
                <span className="text-foreground font-medium">
                  $390.000 ARS / mes
                </span>
              </p>
              <FunnelForm />
            </div>
          </DialogContent>
        </Dialog>

        {/* Testimonios debajo del CTA, como refuerzo */}
        <div className="container mx-auto px-6 max-w-6xl relative z-10 mt-24">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-medium tracking-tight text-foreground mb-3">
              Lo que dicen quienes ya lo usan
            </h3>
            <p className="text-muted-foreground">
              Mientras completes el formulario, mirá los resultados de otros
              negocios.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orderedTestimonials.map((testimonial, index) =>
              testimonial.videoUrl ? (
                <motion.div
                  key={`v-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <div className="bg-[#0F0F11] border border-white/5 rounded-[16px] p-5 md:p-6 hover:border-white/10 transition-all duration-300 h-full flex flex-col">
                    <Dialog
                      onOpenChange={(open) => !open && setActiveVideo(null)}
                    >
                      <DialogTrigger asChild>
                        <div
                          className="relative w-full h-32 mb-4 rounded-[12px] overflow-hidden cursor-pointer group/vid border border-white/10 bg-[#08080A] shrink-0"
                          onClick={() => setActiveVideo(testimonial.videoUrl)}
                        >
                          <video
                            src={`${testimonial.videoUrl}#t=0.001`}
                            className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover/vid:opacity-70 transition-opacity duration-300"
                            muted
                            playsInline
                            preload="metadata"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover/vid:bg-black/20 transition-colors duration-300">
                            <div className="w-11 h-11 rounded-full bg-[#7C3AED]/20 flex items-center justify-center backdrop-blur-md border border-[#7C3AED]/30 group-hover/vid:scale-110 transition-transform duration-300">
                              <Play className="w-5 h-5 text-[#7C3AED] ml-1 fill-current" />
                            </div>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl bg-black border-white/10 p-0 overflow-hidden">
                        <DialogTitle className="sr-only">
                          Video de testimonio {testimonial.name}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                          Testimonio en video de {testimonial.name} de{" "}
                          {testimonial.role}
                        </DialogDescription>
                        {activeVideo === testimonial.videoUrl && (
                          <div className="relative w-full aspect-video [&_.plyr]:h-full [&_.plyr__video-wrapper]:h-full [&_video]:object-contain bg-black">
                            <Plyr
                              source={{
                                type: "video",
                                sources: [
                                  {
                                    src: testimonial.videoUrl,
                                    type: "video/mp4",
                                  },
                                ],
                              }}
                              options={{
                                autoplay: true,
                                controls: [
                                  "play-large",
                                  "play",
                                  "progress",
                                  "current-time",
                                  "mute",
                                  "volume",
                                  "fullscreen",
                                ],
                              }}
                            />
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                    <div className="flex-grow mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <Quote className="w-5 h-5 md:w-6 md:h-6 text-[#7C3AED]/20" />
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className="w-3 h-3 fill-[#FBBF24] text-[#FBBF24]"
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-[#FAFAFA] text-[14px] md:text-[15px] leading-[1.5] md:leading-[1.6] italic">
                        "{testimonial.quote}"
                      </p>
                    </div>
                    <div className="flex items-center gap-2 md:gap-3 mt-auto">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#131316] border border-white/10 flex items-center justify-center shrink-0">
                        <span className="text-[#FAFAFA] font-medium text-[11px] md:text-[12px] tracking-wider">
                          {testimonial.initials}
                        </span>
                      </div>
                      <div>
                        <p className="text-[#FAFAFA] font-medium text-[13px] md:text-[14px] leading-tight">
                          {testimonial.name}
                        </p>
                        <p className="text-[#A1A1AA] text-[11px] md:text-[12px] leading-tight mt-0.5">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={`t-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <div className="bg-[#0F0F11] border border-white/5 rounded-[16px] p-5 md:p-6 hover:border-white/10 transition-all duration-300 h-full flex flex-col">
                    <div className="mb-4 md:mb-6 flex-grow">
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <Quote className="w-5 h-5 md:w-6 md:h-6 text-[#7C3AED]/20" />
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className="w-3 h-3 md:w-3.5 md:h-3.5 fill-[#FBBF24] text-[#FBBF24]"
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-[#FAFAFA] text-[14px] md:text-[15px] leading-[1.5] md:leading-[1.6] italic">
                        "{testimonial.quote}"
                      </p>
                    </div>
                    <div className="flex items-center gap-2 md:gap-3 mt-auto">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#131316] border border-white/10 flex items-center justify-center shrink-0">
                        <span className="text-[#FAFAFA] font-medium text-[11px] md:text-[12px] tracking-wider">
                          {testimonial.initials}
                        </span>
                      </div>
                      <div>
                        <p className="text-[#FAFAFA] font-medium text-[13px] md:text-[14px] leading-tight">
                          {testimonial.name}
                        </p>
                        <p className="text-[#A1A1AA] text-[11px] md:text-[12px] leading-tight mt-0.5">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ),
            )}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={openForm}
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold hover:-translate-y-[2px] transition-all hover:shadow-[0_12px_32px_rgba(16,185,129,0.3)] rounded-xl px-10 py-4 text-lg inline-flex items-center justify-center gap-2"
            >
              Ver cómo funciona
            </button>
            <p className="text-sm text-muted-foreground mt-4 flex items-center justify-center gap-2">
              <GoogleLogo className="w-4 h-4" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              Garantía de 30 días · Sin contrato
            </p>
          </div>
        </div>
      </main>

      <footer className="bg-[#08080A] py-8 border-t border-white/10">
        <div className="container mx-auto px-6 max-w-[1200px] flex flex-col items-center gap-4">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              to="/terminos"
              className="text-[#71717A] hover:text-[#FAFAFA] transition-colors text-[13px]"
            >
              Términos
            </Link>
            <Link
              to="/privacidad"
              className="text-[#71717A] hover:text-[#FAFAFA] transition-colors text-[13px]"
            >
              Privacidad
            </Link>
            <Link
              to="/reembolso"
              className="text-[#71717A] hover:text-[#FAFAFA] transition-colors text-[13px]"
            >
              Reembolsos
            </Link>
          </div>
          <p className="text-[#71717A] font-mono text-[12px] tracking-wider uppercase text-center">
            © 2026 CreceLocal. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
